import { toast } from "sonner";
import i18n from "../i18n";
import { matchesAccept } from "./useAssetDrop";

/**
 * Coller (⌘V / Ctrl+V) une image = la DÉPOSER sur la zone de dépôt que survole la souris.
 *
 * POURQUOI UN AIGUILLAGE GLOBAL plutôt qu'un onPaste par zone : l'app compte une trentaine de
 * zones de dépôt, dont une vingtaine avec leur propre onDrop (Bible, moodboard, création de
 * projet…). Et un collage n'atteint jamais une <div> de dépôt : l'événement `paste` part de
 * l'élément qui a le FOCUS (le textarea du prompt, ou body), pas de celui qu'on survole. On
 * convertit donc le collage en un `drop` sur la zone survolée : elle le reçoit par le MÊME chemin
 * qu'un fichier glissé depuis le Finder — envoi, filtres et messages compris — sans être réécrite.
 *
 * Une zone s'inscrit en portant `data-paste-drop="image,video"` (familles acceptées ; vide =
 * l'appelant trie lui-même) — `useAssetDrop` le pose sur toutes les siennes. LISTE EXPLICITE, pas
 * « l'élément sous la souris » : les grilles de réordonnancement, la timeline ou la carte entière
 * d'un plan ont aussi un onDrop, qui ferait preventDefault puis ne ferait rien d'un fichier — ou
 * l'enregistrerait sur le plan survolé par hasard.
 */

const ZONE = "[data-paste-drop]";
const PASTE_KEY = /Mac|iPhone|iPad/.test(typeof navigator !== "undefined" ? navigator.platform : "") ? "⌘V" : "Ctrl+V";

// Dernière position de la souris. EFFACÉE quand la fenêtre perd la main (capture ⌘⇧4, ⌘Tab) ou que
// la souris en sort : on ne vise jamais une zone avec une position périmée, il faut un nouveau
// mouvement — sinon l'image partirait sur la zone survolée AVANT d'aller faire la capture.
let pointer = null;

const NON_TEXT_INPUTS = new Set(["button", "checkbox", "radio", "file", "range", "color", "submit", "reset", "image", "hidden"]);

function isTextField(el) {
  if (!el || el.disabled || el.readOnly) return false;
  if (el.tagName === "TEXTAREA") return true;
  return el.tagName === "INPUT" && !NON_TEXT_INPUTS.has((el.type || "").toLowerCase());
}

// Une capture d'écran arrive nommée « image.png » : dix collages porteraient le même nom dans la
// bibliothèque. On horodate ce nom générique ; un vrai nom de fichier (copié du Finder) est gardé.
function withPastedName(file, index) {
  if (!/^image\.\w+$/i.test(file.name || "")) return file;
  const ext = file.name.split(".").pop();
  const d = new Date(), p = (n) => String(n).padStart(2, "0");   // heure LOCALE (toISOString serait en UTC)
  const stamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
  try {
    return new File([file], `image-collee-${stamp}${index ? `-${index + 1}` : ""}.${ext}`, { type: file.type });
  } catch { return file; }
}

function dropEventOf(dataTransfer, at) {
  // clientX/Y : certaines zones placent le dépôt d'après la position (TimelinePage).
  const init = { bubbles: true, cancelable: true, clientX: at.x, clientY: at.y, dataTransfer };
  try { return new DragEvent("drop", init); } catch { /* navigateur sans constructeur DragEvent */ }
  const ev = new Event("drop", { bubbles: true, cancelable: true });
  Object.defineProperty(ev, "dataTransfer", { value: dataTransfer });
  return ev;
}

function onPaste(e) {
  // Une zone qui gère déjà le collage elle-même (pièces jointes de l'agent) a la main.
  if (e.defaultPrevented) return;
  const clip = e.clipboardData;
  const files = Array.from(clip?.files || []);
  if (!files.length) return;   // du texte : collage natif, on ne touche à rien
  // Éditeur riche : il sait insérer une image lui-même.
  if (e.target?.isContentEditable) return;
  let text = "";
  try { text = clip.getData("text/plain") || ""; } catch { /* ignore */ }
  // Le presse-papiers porte AUSSI du texte (sélection Keynote avec son aperçu image…) : dans un champ
  // texte, c'est ce texte qu'on colle — la souris qui repose par hasard sur une zone ne doit pas
  // détourner le collage. Sauf quand ce texte n'est que le NOM du fichier copié dans le Finder (Chrome
  // expose le vrai fichier ET son nom) : c'est un fichier qu'on colle.
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);   // plusieurs fichiers = un nom par ligne
  const onlyFileNames = lines.length > 0 && lines.every((l) => files.some((f) => f.name === l));
  if (isTextField(e.target) && text.trim() && !onlyFileNames) return;

  // La zone sous la souris d'abord (on survole puis ⌘V), à défaut celle qui contient le focus.
  const hit = pointer ? document.elementFromPoint(pointer.x, pointer.y) : null;
  const zone = hit?.closest?.(ZONE) || document.activeElement?.closest?.(ZONE) || null;
  if (!zone) {
    // Page sans aucune zone (connexion, tableau de bord…) : l'aide inviterait à survoler ce qui n'existe pas.
    if (document.querySelector(ZONE)) {
      toast.info(i18n.t("pasteToDrop.hoverZone", { key: PASTE_KEY }));
    }
    return;
  }
  e.preventDefault();
  // Bouton désactivé pendant un envoi : le navigateur ne lui livrerait pas le dépôt.
  if (zone.matches(":disabled") || zone.getAttribute("data-paste-drop") === "off") {
    toast.info(i18n.t("pasteToDrop.busy"));
    return;
  }
  const accept = (zone.getAttribute("data-paste-drop") || "").split(",").map((t) => t.trim()).filter(Boolean);
  const accepted = accept.length ? files.filter((f) => matchesAccept(f, accept)) : files;
  if (!accepted.length) {
    toast.error(i18n.t("pasteToDrop.acceptsOnly", { types: accept.join(", ") }));
    return;
  }
  let dataTransfer;
  try {
    dataTransfer = new DataTransfer();
    accepted.map(withPastedName).forEach((f) => dataTransfer.items.add(f));
  } catch {
    toast.error(i18n.t("pasteToDrop.unsupported"));
    return;
  }
  const r = zone.getBoundingClientRect();
  const at = pointer || { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  // dispatchEvent renvoie false quand le onDrop de la zone a fait preventDefault, c.-à-d. a pris le dépôt.
  if (zone.dispatchEvent(dropEventOf(dataTransfer, at))) {
    toast.error(i18n.t("pasteToDrop.notTaken"));
  }
}

const track = (e) => { pointer = { x: e.clientX, y: e.clientY }; };
const leave = (e) => { if (!e.relatedTarget) pointer = null; };
const forget = () => { pointer = null; };
const hide = () => { if (document.hidden) pointer = null; };
const PASSIVE = { capture: true, passive: true };

/** À appeler une fois au démarrage de l'app (App.js) ; renvoie le désabonnement (StrictMode, rechargement à chaud). */
export function installPasteToDrop() {
  if (typeof document === "undefined") return undefined;
  window.addEventListener("pointermove", track, PASSIVE);
  window.addEventListener("pointerdown", track, PASSIVE);
  window.addEventListener("pointerout", leave, PASSIVE);
  window.addEventListener("blur", forget);
  document.addEventListener("visibilitychange", hide);
  // Bouillonnement sur document : passe APRÈS les onPaste de React (délégués au conteneur racine
  // et aux conteneurs de portail), d'où le test defaultPrevented ci-dessus.
  document.addEventListener("paste", onPaste);
  return () => {
    window.removeEventListener("pointermove", track, PASSIVE);
    window.removeEventListener("pointerdown", track, PASSIVE);
    window.removeEventListener("pointerout", leave, PASSIVE);
    window.removeEventListener("blur", forget);
    document.removeEventListener("visibilitychange", hide);
    document.removeEventListener("paste", onPaste);
  };
}

export default installPasteToDrop;
